import { useState, useEffect, useRef, useCallback } from 'react';

import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { NavLink } from '@/components/NavLink';
import {
  Shield, ChevronDown, Home, Sun, Moon, Search, Menu, X, LogOut,
  Keyboard, Settings, Map, BarChart3, Code2, Plug, HelpCircle, ShoppingBag,
} from 'lucide-react';
import { AIToggle } from '@/components/AIToggle';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { CommandPalette } from '@/components/CommandPalette';
import { ActivityFeed } from '@/components/ActivityFeed';
import { WhatsNew } from '@/components/WhatsNew';
import { KeyboardShortcutsDialog } from '@/components/KeyboardShortcuts';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { EnvironmentSwitcher } from '@/components/EnvironmentSwitcher';
import { useAuth } from '@/hooks/use-auth';
import { useKeepAlive } from '@/hooks/use-keep-alive';
import { OnboardingWizard } from '@/components/OnboardingWizard';
import { HelpLauncher } from '@/components/HelpLauncher';
import { FeatureRequestWidget } from '@/components/FeatureRequestWidget';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/* ─── Navigation architecture ────────────────────────────────────
   Philosophy: 3 primary destinations visible at all times.
   Everything else is reachable via Home hub cards or ⌘K search.
   Settings lives behind a gear icon — not a nav group.
   ─────────────────────────────────────────────────────────────── */

interface NavItem { to: string; label: string; icon: any }

const PRIMARY_NAV: NavItem[] = [
  { to: '/home', label: 'Home', icon: Home },
  { to: '/reports', label: 'Reports', icon: ShoppingBag },
  { to: '/studio', label: 'Map Studio', icon: Map },
  { to: '/dashboard', label: 'Analytics', icon: BarChart3 },
];

const SETTINGS_ITEMS: NavItem[] = [
  { to: '/crm', label: 'CRM Connections', icon: Plug },
  { to: '/team', label: 'Team', icon: Settings },
  { to: '/api-keys', label: 'API Keys', icon: Code2 },
  { to: '/security', label: 'Security Center', icon: Shield },
  { to: '/privacy', label: 'Privacy & GDPR', icon: Shield },
  { to: '/docs', label: 'Documentation', icon: HelpCircle },
];

function SettingsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isActive = SETTINGS_ITEMS.some(i => location.pathname === i.to);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={cn(
          "h-8 w-8 flex items-center justify-center rounded-md transition-colors",
          isActive
            ? "bg-sidebar-accent text-sidebar-foreground"
            : "text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground"
        )}
        title="Settings"
        aria-label="Settings menu"
      >
        <Settings className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1.5 w-52 bg-popover border border-border rounded-xl shadow-xl shadow-black/10 py-1.5 z-50">
          {SETTINGS_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-popover-foreground/70 hover:bg-accent hover:text-accent-foreground transition-colors"
              activeClassName="bg-accent text-accent-foreground font-medium"
            >
              <item.icon className="h-3.5 w-3.5" />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  useKeepAlive();
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('sf-theme');
      if (stored) return stored === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('sf-onboarded') && location.pathname === '/home');
  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    localStorage.setItem('sf-onboarded', 'true');
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('sf-theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(o => !o);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault();
        setShortcutsOpen(o => !o);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        setFocusMode(f => !f);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        setDark(d => !d);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
        <header className="h-11 [@media(max-height:600px)]:h-8 flex items-center justify-between px-3 sm:px-5 bg-sidebar shrink-0 relative z-50 transition-[height] duration-150">
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Mobile hamburger */}
            <button
              type="button"
              className="lg:hidden h-8 w-8 flex items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen
                ? <X className="h-4.5 w-4.5 text-sidebar-foreground" />
                : <Menu className="h-4.5 w-4.5 text-sidebar-foreground" />
              }
            </button>

            <Link to="/home" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-[15px] font-bold text-sidebar-foreground tracking-tight [@media(max-height:600px)]:text-[13px]">Boltway<sup className="text-[8px] align-super opacity-60">™</sup></span>
              <span className="hidden xl:inline text-[9px] font-medium text-sidebar-foreground/40 uppercase tracking-wider pl-1.5 ml-0.5">/</span>
              <span className="hidden xl:inline text-[9px] font-medium text-sidebar-foreground/40 uppercase tracking-wider pl-1">Boltway Brain</span>
            </Link>
            <OrgSwitcher />
            <div className="hidden lg:block h-5 w-px bg-sidebar-border" />

            {/* Desktop nav — just 3 primary items */}
            <nav className="hidden lg:flex items-center gap-1">
              {PRIMARY_NAV.map(item => {
                const isReports = item.to === '/reports';
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 [@media(max-height:600px)]:px-2 [@media(max-height:600px)]:py-1 rounded-md text-[13px] [@media(max-height:600px)]:text-[11px] font-medium transition-colors",
                      isReports
                        ? "border border-success/40 bg-success/10 text-success shadow-[0_0_16px_-6px_hsl(var(--success))] hover:bg-success/15"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    )}
                    activeClassName={isReports ? "bg-success/15 text-success" : "bg-sidebar-accent text-sidebar-foreground"}
                  >
                    <item.icon className="h-3.5 w-3.5" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => setCmdOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-sidebar-foreground/50 hover:bg-sidebar-accent transition-colors border border-sidebar-border/50"
              aria-label="Open search"
            >
              <Search className="h-4 w-4" />
              <span className="hidden md:inline text-[12px] text-sidebar-foreground/40">Search…</span>
              <kbd className="hidden md:inline text-[10px] px-1.5 py-0.5 rounded bg-sidebar-foreground/10 text-sidebar-foreground/40 font-mono">⌘K</kbd>
            </button>

            <ActivityFeed />
            <WhatsNew />

            <button
              onClick={() => setDark(d => !d)}
              className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors"
              title={dark ? 'Light mode (⌘D)' : 'Dark mode (⌘D)'}
              aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {dark ? <Sun className="h-4 w-4 text-sidebar-foreground/60" /> : <Moon className="h-4 w-4 text-sidebar-foreground/60" />}
            </button>

            <button
              onClick={() => setShortcutsOpen(true)}
              className="hidden sm:flex h-8 w-8 items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors"
              title="Keyboard Shortcuts (⌘?)"
            >
              <Keyboard className="h-4 w-4 text-sidebar-foreground/50" />
            </button>

            <AIToggle />
            <SettingsDropdown />

            {user && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-[13px] hover:bg-sidebar-accent rounded-md px-2 sm:px-3 py-1.5 transition-colors"
                    aria-label="Profile menu"
                  >
                    <div className="h-7 w-7 rounded-full bg-sidebar-foreground/10 border border-sidebar-foreground/20 flex items-center justify-center text-[12px] font-semibold text-sidebar-foreground" aria-hidden="true">
                      {user.email?.[0]?.toUpperCase() ?? 'A'}
                    </div>
                    <span className="text-sidebar-foreground/70 hidden md:inline truncate max-w-[100px]">
                      {user.user_metadata?.display_name ?? user.email?.split('@')[0] ?? 'Account'}
                    </span>
                    <ChevronDown className="h-3 w-3 text-sidebar-foreground/40 hidden sm:block" aria-hidden="true" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-48 p-1">
                  <div className="px-3 py-2 border-b border-border mb-1">
                    <p className="text-[12px] font-medium text-foreground truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={signOut}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </button>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </header>

      {/* Focus mode exit hint */}
      {focusMode && (
        <div className="fixed top-2 right-2 z-50 opacity-30 hover:opacity-100 transition-opacity">
          <button
            onClick={() => setFocusMode(false)}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-card border border-border text-[10px] text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
            Exit Focus (⌘.)
          </button>
        </div>
      )}

      {/* Mobile nav drawer — simplified */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="fixed top-11 left-0 w-64 h-[calc(100vh-2.75rem)] bg-sidebar border-r border-sidebar-border z-40 lg:hidden overflow-y-auto">
            <div className="p-3 space-y-1">
              {PRIMARY_NAV.map(item => {
                const isReports = item.to === '/reports';
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 rounded-lg text-[14px] font-medium transition-colors",
                      isReports
                        ? "border border-success/40 bg-success/10 text-success shadow-[0_0_16px_-6px_hsl(var(--success))] hover:bg-success/15"
                        : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                    )}
                    activeClassName={isReports ? "bg-success/15 text-success" : "bg-sidebar-accent text-sidebar-foreground"}
                  >
                    <item.icon className="h-4.5 w-4.5" />
                    {item.label}
                  </NavLink>
                );
              })}
              <div className="h-px bg-sidebar-border my-3" />
              <p className="px-3 py-1.5 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-wider">Settings</p>
              {SETTINGS_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                  activeClassName="bg-sidebar-accent text-sidebar-foreground"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        </>
      )}

      {/* Breadcrumbs */}
      <Breadcrumbs />

      {/* Page content */}
      <main id="main-content" className="flex-1 overflow-auto" role="main" aria-label="Page content">
        <Outlet />
      </main>

      {/* Status bar */}
      {!focusMode && (
        <footer className="h-7 hidden sm:flex [@media(max-height:600px)]:!hidden items-center justify-between px-4 bg-card border-t border-border text-[11px] text-muted-foreground shrink-0 tabular-nums">
          <div className="flex items-center gap-5">
            <span>Org: <strong className="text-foreground font-medium">ACME Corp</strong></span>
            <EnvironmentSwitcher />
          </div>
          <div className="flex items-center gap-5">
            <span>Data trust: <strong className="text-foreground font-medium">91%</strong></span>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-success" />
              <span>Live Intelligence Active</span>
            </div>
            <span className="opacity-60">boltway.ai / Boltway Brain</span>
          </div>
        </footer>
      )}

      {showOnboarding && <OnboardingWizard onDismiss={dismissOnboarding} />}
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
      <KeyboardShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <HelpLauncher />
      <FeatureRequestWidget />
    </div>
  );
}
